import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UsersThree, CheckCircle } from "@phosphor-icons/react";
import { SectionShell } from "../../styleguide/Primitives";
import { Showcase } from "../LibraryPrimitives";
import { SubjectCard, LessonCard } from "../cards";
import { MasteryBadge } from "../progress";
import { BloomButton } from "../../styleguide/ui/BloomButton";
import { Mascot } from "../../styleguide/Mascot";
import { cn } from "@/lib/utils";

const BANDS = {
  y12: {
    label: "Years 1–2", cardSize: "lg", btnSize: "lg", mascot: true, animated: true,
    points: ["Larger cards & buttons", "Lots of friendly illustration", "More expressive animation", "One clear focus per screen"],
  },
  y34: {
    label: "Years 3–4", cardSize: "md", btnSize: "md", mascot: true, animated: false,
    points: ["Balanced sizing", "Illustration supports, not leads", "Standard, delightful motion", "More content per screen"],
  },
  y56: {
    label: "Years 5–6", cardSize: "sm", btnSize: "md", mascot: false, animated: false,
    points: ["Cleaner, mature layouts", "Minimal decoration", "Reduced, subtle animation", "Denser, efficient information"],
  },
};

export function AgeAdaptationSection() {
  const [band, setBand] = useState("y12");
  const b = BANDS[band];

  return (
    <SectionShell
      id="age"
      eyebrow="Age adaptation"
      eyebrowIcon={UsersThree}
      title="One system, growing up"
      description="The very same components mature with the child — bigger and more playful for Year 1, cleaner and calmer by Year 6. Switch the age band to see the same building blocks adapt."
    >
      <div className="mb-6 inline-flex flex-wrap gap-1.5 rounded-full bg-muted p-1.5">
        {Object.entries(BANDS).map(([key, val]) => (
          <button
            key={key}
            onClick={() => setBand(key)}
            data-testid={`age-${key}`}
            className={cn(
              "min-h-[44px] rounded-full px-5 text-sm font-bold transition-all duration-200",
              band === key ? "bg-card text-primary-strong shadow-soft" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {val.label}
          </button>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
        <Showcase title="Adaptive preview" tone="muted">
          <AnimatePresence mode="wait">
            <motion.div
              key={band}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={cn("flex flex-col", band === "y12" ? "gap-5" : band === "y34" ? "gap-4" : "gap-3")}
            >
              {b.mascot && (
                <div className="flex items-center gap-3 rounded-3xl bg-card p-4 shadow-soft">
                  <Mascot size={band === "y12" ? 72 : 56} mood="encouraging" animated={b.animated} />
                  <p className={cn("font-display font-extrabold", band === "y12" ? "text-xl" : "text-lg")}>
                    {band === "y12" ? "Let's play and learn!" : "Ready for today's lesson?"}
                  </p>
                </div>
              )}
              <div className={cn("grid gap-4", band === "y56" ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-2")}>
                <SubjectCard subject="maths" size={b.cardSize} done={9} lessons={24} />
                <SubjectCard subject="reading" size={b.cardSize} done={16} lessons={18} />
              </div>
              <LessonCard subject="english" title="Spelling sounds" state="current" />
              <div className="flex items-center justify-between">
                <MasteryBadge level={band === "y12" ? "growing" : band === "y34" ? "strong" : "mastered"} />
                <BloomButton size={b.btnSize}>Continue</BloomButton>
              </div>
            </motion.div>
          </AnimatePresence>
        </Showcase>

        <Showcase title={`${b.label} — what changes`}>
          <ul className="space-y-3">
            {b.points.map((p) => (
              <li key={p} className="flex items-start gap-3 font-semibold text-foreground">
                <CheckCircle size={22} weight="fill" className="mt-0.5 flex-shrink-0 text-primary-strong" />
                {p}
              </li>
            ))}
          </ul>
          <div className="mt-6 rounded-2xl bg-muted/40 p-4">
            <p className="text-sm font-medium leading-relaxed text-muted-foreground">
              Tokens, colours and components never change — only their <strong className="text-foreground">scale, density and expressiveness</strong> do. That keeps the brand consistent while the experience matures naturally.
            </p>
          </div>
        </Showcase>
      </div>
    </SectionShell>
  );
}

export default AgeAdaptationSection;
